import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, Heart, Star, Users } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const LOGO_URL = "/rumeli-logo.png";

const values = [
  {
    icon: Target,
    title: "Disiplin",
    desc: "Sporda ve hayatta başarının temelini oluşturan disiplin anlayışını aşılıyor, geleceğin liderlerini yetiştiriyoruz.",
  },
  {
    icon: Heart,
    title: "Tutku",
    desc: "Spora olan tutkumuz, her antrenman ve müsabakada en iyi performansı sergileme motivasyonu veriyor.",
  },
  {
    icon: Star,
    title: "Mükemmellik",
    desc: "Sürekli gelişim ve mükemmellik arayışı ile sporcularımızın potansiyellerini en üst seviyeye taşımayı hedefliyoruz.",
  },
  {
    icon: Users,
    title: "Takım Ruhu",
    desc: "Birlikte başarma kültürü ile sporcularımızın sosyal becerilerini güçlendiriyoruz.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
      <SEOHead
        title="Hakkımızda - Ataşehir, Kadıköy, Maltepe Spor Kulübü"
        description="İRÜ Spor Kulübü hakkında bilgi edinin. İstanbul Rumeli Üniversitesi bünyesinde Ataşehir, Kadıköy, Maltepe bölgesinde voleybol, basketbol ve cimnastik eğitimi veren profesyonel spor kulübü."
        keywords="IRU spor kulübü hakkında, Rumeli Üniversitesi spor, Anadolu yakası spor kulübü, Kadıköy spor eğitimi, Ataşehir spor kulübü, Maltepe spor"
        canonical="/hakkimizda"
      />
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-gold">Hakkımızda</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mt-2">
            Sporun<br /><span className="text-gold">Akademik Hali</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Hikayemiz</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                İRÜ Spor Kulübü
              </h2>
              <div className="mt-8 space-y-4 text-gray-600 leading-relaxed">
                <p>
                  İRÜ Spor Kulübü, İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi bünyesinde
                  faaliyet gösteren, çocukların ve gençlerin spor alanında gelişmelerini sağlayan
                  profesyonel bir spor kulübüdür.
                </p>
                <p>
                  Deneyimli antrenör kadromuz eşliğinde Voleybol, Basketbol ve Cimnastik
                  branşlarında eğitim vermekteyiz. Amacımız, sporcularımızın hem fiziksel
                  hem de zihinsel gelişimlerine katkı sağlamak ve onları geleceğin şampiyonları
                  olarak yetiştirmektir.
                </p>
                <p>
                  Üniversite bünyesinde faaliyet göstermemiz, sporcularımıza modern tesisler,
                  akademik destek ve güvenli bir ortam sunmamızı sağlamaktadır.
                </p>
              </div>
              <Link to="/on-kayit" className="inline-block mt-8">
                <Button
                  data-testid="about-register-btn"
                  className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-6"
                >
                  Bize Katılın <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 sm:w-80 sm:h-80 bg-burgundy/5 border border-burgundy/10 flex items-center justify-center">
                  <img src={LOGO_URL} alt="Rumeli Üniversitesi" className="w-48 sm:w-64 h-auto" />
                </div>
                <div className="absolute -bottom-4 -right-4 w-64 h-64 sm:w-80 sm:h-80 border border-burgundy/20 -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 lg:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Misyonumuz</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
              Sağlam Kafa, Sağlam Vücut
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed">
              Çocukların ve gençlerin spor yoluyla disiplin, takım ruhu ve özgüven kazanmalarını
              sağlayarak sağlıklı nesiller yetiştirmeyi hedefliyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {values.map((value) => (
              <div
                key={value.title}
                data-testid={`value-${value.title.toLowerCase()}`}
                className="p-8 bg-white border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 mb-6">
                  <value.icon className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                  {value.title}
                </h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-burgundy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
            Ailemize Katılın
          </h2>
          <p className="mt-4 text-white/70">
            Çocuğunuzun spor yolculuğunda ona eşlik etmeye hazır mısınız?
          </p>
          <Link to="/on-kayit">
            <Button
              data-testid="about-cta-btn"
              className="mt-8 bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-10 py-6"
            >
              Ön Kayıt Formu <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
